import React from 'react';
import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'error';
  checkboxLabel?: string;
  defaultChecked?: boolean;
}

export interface ConfirmResult {
  checked: boolean;
}

interface ConfirmDialog extends ConfirmOptions {
  open: boolean;
  onConfirm: (checked: boolean) => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialog> = ({
  open,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  checkboxLabel,
  defaultChecked = false,
  onConfirm,
  onCancel,
}) => {
  const [checked, setChecked] = useState(defaultChecked);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        {checkboxLabel && (
          <FormControlLabel
            control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
            label={checkboxLabel}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{cancelText}</Button>
        <Button onClick={() => onConfirm(checked)} color={confirmColor} autoFocus>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useConfirm = () => {
  const [dialog, setDialog] = useState<ConfirmOptions & { resolve: (value: ConfirmResult | false) => void }>();

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<ConfirmResult | false>((resolve) => {
      setDialog({ ...options, resolve });
    });
  }, []);

  const handleClose = useCallback(() => {
    if (dialog) {
      dialog.resolve(false);
      setDialog(undefined);
    }
  }, [dialog]);

  const handleConfirm = useCallback(
    (checked: boolean) => {
      if (dialog) {
        dialog.resolve({ checked });
        setDialog(undefined);
      }
    },
    [dialog],
  );

  return {
    confirm,
    ConfirmationDialog: dialog ? (
      <ConfirmDialog
        open={true}
        title={dialog.title}
        description={dialog.description}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        confirmColor={dialog.confirmColor}
        checkboxLabel={dialog.checkboxLabel}
        defaultChecked={dialog.defaultChecked}
        onConfirm={handleConfirm}
        onCancel={handleClose}
      />
    ) : null,
  };
};
